import { ComponentType } from 'react';

import { IconProps } from '@components/icons';
import { IconArray } from '@components/icons/array';
import { IconArrayObjects } from '@components/icons/array-objects';
import { IconArrayStrings } from '@components/icons/array-strings';
import { IconBoolean } from '@components/icons/boolean';
import { IconEmail } from '@components/icons/email';
import { IconFile } from '@components/icons/file';
import { IconObject } from '@components/icons/object';
import { IconTextFormatNumber } from '@components/icons/text-format-number';
import { IconTextShort } from '@components/icons/text-short';
import { JsonValue } from '@components/json-editor';
import { getScopedTranslate } from '@locale/translate';
import { validateEmail } from '@utils/validators';

export const ALL_VALUE_TYPES = [
  'string',
  'number',
  'boolean',
  'array',
  'array-strings',
  'array-objects',
  'object',
  'email',
  'file',
] as const;

const TYPE_TO_ICON_MAP: Record<ValueTypes, ComponentType<IconProps>> = {
  'array-strings': IconArrayStrings,
  'array-objects': IconArrayObjects,
  array: IconArray,
  object: IconObject,
  file: IconFile,
  string: IconTextShort,
  email: IconEmail,
  number: IconTextFormatNumber,
  boolean: IconBoolean,
};

const TYPE_TO_NAME_MAP: Record<ValueTypes, keyof (typeof locale)['en']> = {
  array: 'arrays',
  'array-objects': 'arrays',
  'array-strings': 'arrays',

  boolean: 'booleans',
  email: 'emails',
  number: 'numbers',
  object: 'objects',
  file: 'files',
  string: 'strings',
};

export type ValueTypes = (typeof ALL_VALUE_TYPES)[number];

export const getType = (value: JsonValue) => {
  let type: ValueTypes = 'string';

  if (Array.isArray(value)) {
    const allStrings = value.every((v) => typeof v === 'string');
    const allObjects = value.every((v) => v !== null && typeof v === 'object' && !Array.isArray(v));

    if (allStrings) {
      type = 'array-strings';
    } else if (allObjects) {
      type = 'array-objects';
    } else {
      type = 'array';
    }
  } else if (value !== null && typeof value === 'object') {
    type = 'object';
  } else if (typeof value === 'string') {
    if (validateEmail(value)) {
      type = 'email';
    } else {
      type = 'string';
    }
  } else if (typeof value === 'number') {
    type = 'number';
  } else if (typeof value === 'boolean') {
    type = 'boolean';
  }

  return type;
};

export const getIconBasedOnType = (
  value: JsonValue,
  showIconForTypes: ValueTypes[]
): ComponentType<IconProps> => {
  const type = getType(value);

  if (!showIconForTypes.includes(type)) {
    return IconTextShort;
  }

  return TYPE_TO_ICON_MAP[type];
};

export const getTypeName = (type: ValueTypes) => {
  const { t } = getScopedTranslate(locale);
  const key = TYPE_TO_NAME_MAP[type] ?? 'strings';
  return t(key);
};

const locale = {
  en: {
    arrays: 'Arrays',
    strings: 'Strings',
    numbers: 'Numbers',
    objects: 'Objects',
    emails: 'Emails',
    booleans: 'Booleans',
    files: 'Files',
  },
};
